import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { formatDateTime, messages } from '@nesc/shared';
import { api, type ListResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/feedback';
import { FilterChips, PageHeader } from '@/components/common/page';
import { tabTriggerClass } from '@/lib/utils';

type AuditLog = {
  id: string;
  actor: { id: string; name: string | null; role: string } | null;
  actionLabel: string;
  targetType: string | null;
  summary: string | null;
  traceId: string | null;
  createdAt: string;
};

type ExecutionLog = {
  id: string;
  occurredAt: string;
  label: string;
  result: string;
  detail: string;
  href: string | null;
};

const TABS = [
  { value: 'audit', label: messages.logs.tabAudit },
  { value: 'collection', label: messages.logs.tabCollection },
  { value: 'crawl', label: messages.logs.tabCrawl },
  { value: 'send', label: messages.logs.tabSend },
  { value: 'ai', label: messages.logs.tabAi },
  { value: 'error', label: messages.logs.tabError },
];

/** S80 ログ */
export const LogsPage = () => {
  const { hasRole } = useAuth();
  const [tab, setTab] = useState('audit');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const visibleTabs = TABS.filter((t) => t.value !== 'error' || hasRole('admin'));

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.logs.title}
        description={messages.logs.maskedNotice}
      />

      <FilterChips className="mb-3">
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="開始日"
          className="h-8 w-auto shrink-0 text-note lg:h-8"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="終了日"
          className="h-8 w-auto shrink-0 text-note lg:h-8"
        />
      </FilterChips>

      <Tabs.Root value={tab} onValueChange={setTab}>
        <Tabs.List className="scroll-x mb-3 flex gap-1 border-b border-border" aria-label="ログの種類">
          {visibleTabs.map((item) => (
            <Tabs.Trigger
              key={item.value}
              value={item.value}
              className={tabTriggerClass}
            >
              {item.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="audit">
          <AuditLogList from={from} to={to} />
        </Tabs.Content>
        {(['collection', 'crawl', 'send', 'ai'] as const).map((kind) => (
          <Tabs.Content key={kind} value={kind}>
            <ExecutionLogList kind={kind} from={from} to={to} />
          </Tabs.Content>
        ))}
        <Tabs.Content value="error">
          <ErrorLogList from={from} to={to} />
        </Tabs.Content>
      </Tabs.Root>
    </>
  );
};

const AuditLogList = ({ from, to }: { from: string; to: string }) => {
  const query = { from: from || undefined, to: to || undefined, limit: 50 };
  const { data, isLoading } = useQuery({
    queryKey: qk.auditLogs(query),
    queryFn: () => api.get<ListResponse<AuditLog>>('/logs/audit', query),
  });

  if (isLoading) return <SkeletonRows rows={6} />;
  if (!data || data.data.length === 0) return <EmptyState title="ログはありません" />;

  return (
    <Card>
      <CardContent className="pt-4 lg:pt-5">
        <ul className="divide-y divide-border">
          {data.data.map((log) => (
            <li key={log.id} className="py-3">
              <details>
                <summary className="flex cursor-pointer flex-wrap items-center gap-2">
                  <span className="tabular w-36 shrink-0 text-note text-foreground-muted">
                    {formatDateTime(log.createdAt)}
                  </span>
                  <span className="text-body font-semibold">{log.actionLabel}</span>
                  <span className="text-note text-foreground-muted">
                    {log.actor?.name ?? 'システム'}
                  </span>
                </summary>
                <div className="mt-2 space-y-1 pl-4 text-note text-foreground-muted">
                  {log.summary && <p>{log.summary}</p>}
                  {log.targetType && <p>対象: {log.targetType}</p>}
                  {log.traceId && (
                    <p className="tabular">
                      {messages.logs.traceId}: {log.traceId}
                    </p>
                  )}
                </div>
              </details>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const ExecutionLogList = ({
  kind,
  from,
  to,
}: {
  kind: 'collection' | 'crawl' | 'send' | 'ai';
  from: string;
  to: string;
}) => {
  const query = { kind, from: from || undefined, to: to || undefined, limit: 50 };
  const { data, isLoading } = useQuery({
    queryKey: qk.executionLogs(query),
    queryFn: () => api.get<ListResponse<ExecutionLog>>('/logs/executions', query),
  });

  if (isLoading) return <SkeletonRows rows={6} />;
  if (!data || data.data.length === 0) return <EmptyState title="ログはありません" />;

  return (
    <Card>
      <CardContent className="pt-4 lg:pt-5">
        <ul className="divide-y divide-border">
          {data.data.map((log) => (
            <li key={log.id} className="flex flex-wrap items-start gap-2 py-3">
              <span className="tabular w-36 shrink-0 text-note text-foreground-muted">
                {formatDateTime(log.occurredAt)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body font-semibold">{log.label}</p>
                <p className="break-all text-note text-foreground-muted">{log.detail}</p>
              </div>
              {log.href && (
                <a
                  href={log.href}
                  className="shrink-0 text-note underline underline-offset-2"
                >
                  {messages.common.detail}
                </a>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const ErrorLogList = ({ from, to }: { from: string; to: string }) => {
  const query = { from: from || undefined, to: to || undefined, limit: 50 };
  const { data, isLoading } = useQuery({
    queryKey: ['logs', 'errors', query],
    queryFn: () => api.get<ListResponse<ExecutionLog>>('/logs/errors', query),
  });

  if (isLoading) return <SkeletonRows rows={4} />;
  if (!data || data.data.length === 0) return <EmptyState title="エラーはありません" />;

  return (
    <Card>
      <CardContent className="pt-4 lg:pt-5">
        <ul className="divide-y divide-border">
          {data.data.map((log) => (
            <li key={log.id} className="space-y-1 py-3">
              <p className="flex flex-wrap items-center gap-2">
                <span className="tabular text-note text-foreground-muted">
                  {formatDateTime(log.occurredAt)}
                </span>
                <span className="text-body font-semibold">{log.label}</span>
              </p>
              <p className="break-all text-note text-foreground-muted">{log.detail}</p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
